/**
 * Chart Type Registry
 * Centralizes chart type configurations from individual chart components
 */

import { lineChartConfig } from './line-chart'
import { stackedAreaChartConfig } from './stacked-area-chart'
import { verticalBarChartConfig } from './vertical-bar-chart'
import { barChartConfig } from './bar-chart'
import { pieDonutChartConfig } from './pie-donut-chart'
import { scatterPlotConfig } from './scatter-plot'
import { kpiChartConfig } from './kpi-chart'
import { pivotTableConfig } from './pivot-table'
import { dataTableConfig } from './data-table'
import { textChartConfig } from './text-chart'
import { headerChartConfig } from './header-chart'
import { pageBreakChartConfig } from './page-break-chart'
import { hasDataFieldTokens } from 'helpers/data-field-tokens'

// Map chart types to their configurations
const CHART_CONFIGS = {
  'line': lineChartConfig,
  'stacked-area': stackedAreaChartConfig,
  'vertical-bar': verticalBarChartConfig,
  'horizontal-bar': barChartConfig,
  'pie-donut': pieDonutChartConfig,
  'scatter-plot': scatterPlotConfig,
  'kpi': kpiChartConfig,
  'pivot-table': pivotTableConfig,
  'data-table': dataTableConfig,
  'text': textChartConfig,
  'header': headerChartConfig,
  'page-break': pageBreakChartConfig
}

// Decorative blocks: they render from their own saved content, never from transaction data, so
// they carry no data source. Mirrors ReportChart::TITLE_EXEMPT_TYPES on the API side, which
// exempts the same three from the report_dataset_id presence check.
export const DATA_SOURCE_EXEMPT_CHART_TYPES = ['text', 'header', 'page-break']

/**
 * Whether a chart of this type draws from a data source (and so needs one bound).
 * A text block is exempt until it quotes the data with a {{Column:aggregation}} token.
 * @param {string} chartType - The chart type identifier
 * @param {string} [textContent] - A text block's content
 * @returns {boolean}
 */
export const chartTypeNeedsDataSource = (chartType, textContent) =>
  !DATA_SOURCE_EXEMPT_CHART_TYPES.includes(chartType) ||
  (chartType === 'text' && hasDataFieldTokens(textContent))

/**
 * Get configuration for a specific chart type
 * @param {string} chartType - The chart type identifier
 * @returns {Object|null} Chart configuration object or null if not found
 */
export const getChartConfig = (chartType) => {
  return CHART_CONFIGS[chartType] || null
}

/**
 * Validate form data for a specific chart type
 * @param {string} chartType - The chart type identifier
 * @param {Object} formData - Object containing all form field values
 * @returns {Object} Validation errors object (empty if valid)
 */
export const validateChartForm = (chartType, formData) => {
  const errors = {}

  // Title validation (not required for decorative text / page-break blocks)
  if (chartType !== 'text' && chartType !== 'page-break' && !formData.title?.trim()) {
    errors.title = 'Title is required'
  }

  // Get chart-specific config and run validation
  const config = getChartConfig(chartType)
  if (config && config.validate) {
    const chartErrors = config.validate(formData)
    Object.assign(errors, chartErrors)
  }

  return errors
}

/**
 * Check if all required data is present to enable chart query
 * @param {string} chartType - The chart type identifier
 * @param {Object} formData - Object containing all form field values
 * @param {boolean} hasChartId - Whether the chart has been saved (has an ID)
 * @returns {boolean} True if query should be enabled
 */
export const isChartQueryEnabled = (chartType, formData, hasChartId) => {
  const config = getChartConfig(chartType)
  if (config && config.isQueryEnabled) {
    return config.isQueryEnabled(formData, hasChartId)
  }
  // Default fallback
  return !!(hasChartId && formData.groupBy)
}

/**
 * Build chart-specific params for API request (for saving to backend)
 * @param {string} chartType - The chart type identifier
 * @param {Object} formData - Object containing all form field values
 * @returns {Object} Chart-specific params object
 */
export const buildChartParams = (chartType, formData) => {
  const config = getChartConfig(chartType)
  if (config && config.buildChartParams) {
    return config.buildChartParams(formData)
  }
  // Default fallback
  return {
    group_by_column_id: formData.groupBy
  }
}

/**
 * Build query params for chart data API request
 * @param {string} chartType - The chart type identifier
 * @param {Object} baseParams - Base transaction query params
 * @param {Object} formData - Object containing all form field values
 * @param {Array} columns - Available columns from the table
 * @returns {Object} Query params for API request
 */
export const buildQueryParams = (chartType, baseParams, formData, columns) => {
  const config = getChartConfig(chartType)
  if (config && config.buildQueryParams) {
    return config.buildQueryParams(baseParams, formData, columns)
  }
  // Default fallback for simple group-by charts
  const groupByCol = columns?.find((c) => c.id === formData.groupBy)
  const params = { ...baseParams }
  params.group_by = groupByCol?.database_column_name
  return params
}

/**
 * Format date labels for chart categories based on date grouping.
 *
 * Rendered in UTC on purpose. A grouped category is a calendar bucket, not an
 * instant: the backend's date_trunc lands on midnight and serializes as
 * "2025-01-01T00:00:00.000Z", so formatting it in the browser's local zone
 * moves it to the previous day in every US timezone — a 2025 monthly chart
 * labelled itself Dec 2024 through Nov 2025. Same rationale (and same fix) as
 * helpers/chart-formatters#formatDateByGrouping and helpers/datetime.
 *
 * @param {Array} categories - Array of date strings
 * @param {string} dateGrouping - Date grouping type (day, week, month, year)
 * @returns {Array} Formatted date labels
 */
const DATE_LABEL_FORMATS = {
  week: { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' },
  month: { month: 'short', year: 'numeric', timeZone: 'UTC' },
  year: { year: 'numeric', timeZone: 'UTC' },
  day: { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
}

export const formatDateLabels = (categories, dateGrouping) => {
  return categories.map((dateStr) => {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr

    return date.toLocaleDateString('en-US', DATE_LABEL_FORMATS[dateGrouping] ?? DATE_LABEL_FORMATS.day)
  })
}

/**
 * Check if chart type supports date formatting
 * @param {string} chartType - The chart type identifier
 * @returns {boolean} True if chart supports date formatting
 */
export const supportsDateFormatting = (chartType) => {
  const config = getChartConfig(chartType)
  return !!(config && config.supportsDateFormatting)
}

/**
 * Render chart-specific options in the modal form
 * @param {string} chartType - The chart type identifier
 * @param {Object} props - Props to pass to the renderOptions function
 * @returns {JSX.Element|null} Chart-specific options UI or null if not found
 */
export const renderChartOptions = (chartType, props) => {
  const config = getChartConfig(chartType)
  if (config && config.renderOptions) {
    return config.renderOptions(props)
  }
  return null
}

/**
 * Sanitize chart params by removing parameters not relevant to the chart type
 * @param {string} chartType - The chart type identifier
 * @param {Object} params - Chart params to sanitize
 * @returns {Object} Sanitized params object containing only relevant parameters for the chart type
 */
export const sanitizeChartParams = (chartType, params) => {
  // Define which params are valid for each chart type
  const validParamsByType = {
    'line': [
      'x_axis_column_id',
      'x_axis_date_grouping',
      'y_axis_column_id',
      'y_axis_aggregation',
      'y_axis_label',
      'y_axis_group_by_column_id',
      'show_totals'
    ],
    'vertical-bar': [
      'x_axis_column_id',
      'x_axis_date_grouping',
      'y_axis_column_id',
      'y_axis_aggregation',
      'y_axis_label',
      'y_axis_group_by_column_id',
      'display_limit'
    ],
    'horizontal-bar': [
      'x_axis_column_id',
      'x_axis_date_grouping',
      'y_axis_column_id',
      'y_axis_aggregation',
      'y_axis_label',
      'y_axis_group_by_column_id',
      'display_limit'
    ],
    'stacked-area': [
      'x_axis_column_id',
      'x_axis_date_grouping',
      'y_axis_column_id',
      'y_axis_aggregation',
      'y_axis_label',
      'y_axis_group_by_column_id'
    ],
    'pie-donut': [
      'group_by_column_id',
      'slice_column_id',
      'slice_aggregation'
    ],
    'scatter-plot': [
      'x_axis_column_id',
      'y_axis_column_id'
    ],
    'kpi': [
      'value_column_id',
      'aggregation_method',
      'analytic_id',
      'has_no_analytics'
    ],
    'pivot-table': [
      'pivot_row_column_id',
      'pivot_row_date_grouping',
      'pivot_row_segment_display',
      'pivot_row_date_format',
      'pivot_column_column_id',
      'column_aggregations',
      'additional_pivot_row_column_ids',
      'additional_pivot_row_date_groupings',
      'additional_pivot_row_segment_displays',
      'additional_pivot_row_date_formats',
      'pivot_inline_groups',
      'pivot_show_totals'
    ],
    'data-table': [
      'columns'
    ],
    'text': [
      'text_content',
      'repeat_on_every_page'
    ],
    'header': [
      'subtitle',
      'background_color',
      'show_logo',
      'title_color',
      'subtitle_color',
      'font_size',
      'text_align',
      'show_divider',
      'logo_size',
      'show_date',
      'repeat_on_every_page'
    ],
    'page-break': []
  }

  const validParams = validParamsByType[chartType] || []

  // Create a new object with only the valid params for this chart type
  const sanitized = {}
  validParams.forEach(param => {
    if (param in params) {
      sanitized[param] = params[param]
    }
  })

  return sanitized
}
